import { useMemo, useCallback } from 'react';

import { useUser } from 'src/hooks/use-user';

import { hasEnhancedSearchLv2Plan } from 'src/utils/plan/enhanced-search-plan';
import {
  getPlanAccessContext,
  isPlanFeatureEnabled,
  resolvePlanServiceValue,
} from 'src/utils/plan/plan-access';
import {
  isBrandTierPlan,
  canCollectInstagramStory,
  getBrandFeatureFixedCountry,
  canUseBrandExclusivePlatforms,
} from 'src/utils/plan/plan-utils';

/**
 * 현재 워크스페이스·플랜 설정 단일 진입점
 */
export function usePlanAccess() {
  const user = useUser();

  const accessContext = useMemo(
    () => getPlanAccessContext(user),
    [user],
  );

  const {
    workspace,
    planId,
    baseConfig,
    config,
    isCustom,
    isReady,
    isExpired,
  } = accessContext;

  /**
   * ⚠️ 열쇠 둘을 선택으로 연다 — 소비처가 `area.accessField` 처럼 없을 수 있는 값을
   * 넘긴다. 안쪽 `isPlanFeatureEnabled` 가 `?.` 로 받아 거짓을 준다.
   */
  const can = useCallback(
    (accessKey?: string | null, accessField?: string | null) => {
      if (!isReady || isExpired) return false;
      return isPlanFeatureEnabled(config, accessKey, accessField);
    },
    [isReady, isExpired, config],
  );

  /** ⚠️ `serviceKey` 는 선택이다 — 소비처가 없을 수 있는 한도 키를 그대로 넘긴다(없으면 `null`) */
  const getLimit = useCallback(
    (serviceKey?: string | null) => resolvePlanServiceValue(config, serviceKey),
    [config],
  );

  return useMemo(() => ({
    user,
    workspace,
    planId,
    baseConfig,
    config,
    planConfig: config,
    limitConfig: config,
    isCustom,
    isReady,
    isExpired,
    can,
    getLimit,
    isBrandTierPlan: isBrandTierPlan(planId),
    canUseBrandExclusivePlatforms: canUseBrandExclusivePlatforms(planId),
    canCollectInstagramStory: canCollectInstagramStory(planId),
    brandFeatureFixedCountry: getBrandFeatureFixedCountry(planId),
    /**
     * 확장검색 레벨2 — 기본 검색 코퍼스(isBasicSearch) 해제 토글 노출 권한.
     * 타겟 키워드 설정의 키워드별 TikTok·Instagram 강화 수집과 **같은 조건**이다
     * (`subscriptionPlan.planService.enhancedSearchLV2` 보유 워크스페이스).
     */
    canUseExtendedSearchLevel2: hasEnhancedSearchLv2Plan(workspace),
  }), [
    user,
    workspace,
    planId,
    baseConfig,
    config,
    isCustom,
    isReady,
    isExpired,
    can,
    getLimit,
  ]);
}
